/**
 * Translation keys eligible for per-tenant display-text overrides.
 * The base/generic wording for these keys lives in the locale files
 * (src/assets/i18n/{lang}.json); this union only controls which keys
 * TenantLabelOverrideService is allowed to overlay per tenant.
 *
 * Add a key here (no other code change required) when a new field
 * needs tenant-configurable wording.
 */
export type TenantLabelOverrideKey =
  | 'ettu_ma_player'
  | 'non_ettu_ma_player'
  | 'non_ettu_ma_player_ettu_ma_passport'
  | 'eu_non_eu'
  | 'eu_non_eu_abbr'
  | 'eu_non_eu_passport'
  | 'eu'
  | 'non_eu'
  | 'license ID'
  | 'email_or_id'
  | 'enter_email_or_id'
  | 'rating'
  | 'rating_leaderboard'
  | 'rating_points';

/**
 * Override text for a subset of {@link TenantLabelOverrideKey} keys, for a single language.
 * Keys absent here fall back to the generic locale-file text.
 */
export type TenantLabelOverrides = Partial<Record<TenantLabelOverrideKey, string>>;

/**
 * Reserved key inside a tenant block that holds {@link BackendLabelOverrides}
 * instead of ngx-translate key overrides. Not a language code.
 */
export const BACKEND_LABELS_KEY = 'backendLabels' as const;

/**
 * Per-language display text for a single immutable, API-supplied label
 * (e.g. "License ID"), keyed by ngx-translate language code.
 */
export type BackendLabelByLang = Record<string, string>;

/**
 * Maps immutable backend/API labels (exactly as the server sends them) to
 * their per-language tenant display text. Used by convertTenantLabel() to
 * relabel text that never passes through ngx-translate keys.
 */
export type BackendLabelOverrides = Record<string, BackendLabelByLang>;

/**
 * Override text for a single tenant, keyed by ngx-translate language code
 * (e.g. 'en', 'da', 'norway' — matches the filenames under src/assets/i18n/),
 * plus the reserved {@link BACKEND_LABELS_KEY} entry for backend-label
 * relabeling.
 */
export type TenantLabelOverridesByLang = Record<
  string,
  TenantLabelOverrides | BackendLabelOverrides
>;

/**
 * Full override map as served by tenant-label-overrides.json, keyed by
 * environment.tenant_Name.
 */
export type TenantLabelOverrideMap = Record<string, TenantLabelOverridesByLang>;
